import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    output,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { ReportBuilderCell } from "./s25.document.report.builder.table.component";
import { Doc } from "./s25.document.const";
import { S25Util } from "../../../util/s25-util";
import { PopoverComponent } from "../../s25-popover/popover.component";
import { DocumentReportBuilderComponent } from "./s25.document.report.builder.component";

@Component({
    standalone: false,
    selector: "s25-ng-document-report-builder-cell",
    template: `
        @if (editable()) {
            <s25-popover
                [popoverTemplate]="editCellTemplate"
                [openTrigger]="'click'"
                [closeTrigger]="'click'"
                [inline]="false"
            >
                <ng-container [ngTemplateOutlet]="cellTemplate" />
            </s25-popover>
        } @else {
            <ng-container [ngTemplateOutlet]="cellTemplate" />
        }

        <ng-template #cellTemplate>
            <div class="style-container" [class.invalid]="editable() && !cell().valid()" [style]="cell().css()">
                @for (item of cell().display(); track $index) {
                    @if (item.type === "text") {
                        <span class="text">{{ item.value }}</span>
                    } @else if (item.type === "functionText") {
                        <span class="functionText">{{ item.value }}</span>
                    } @else if (item.type === "variable") {
                        <span class="variable">{{ item.value }}</span>
                    } @else if (item.type === "subreport" && !!cell().variable().subreportId) {
                        <s25-ng-document-report-builder
                            [documentId]="cell().variable().subreportId"
                            [editable]="false"
                            (reportBuilderChange)="cell().variable().subreport = $event"
                        />
                    } @else if (item.type === "image") {
                        <s25-ng-image [model]="{ imageId: item.image.id }" [style.width.px]="item.image.width" />
                    }
                }
            </div>
        </ng-template>

        <ng-template #editCellTemplate>
            <s25-ng-document-report-builder-cell-editor
                [cell]="cell()"
                [documentType]="documentType()"
                [documentScope]="documentScope()"
                [groupBy]="groupBy()"
                (widthChange)="widthChange.emit($event)"
                (hide)="hidePopoverUntil($event)"
            />
        </ng-template>
    `,
    styles: `
        :host {
            display: block;
            height: 100%;
            overflow: hidden;
        }

        .style-container {
            overflow: hidden;
            height: 100%;
            align-content: end;
            overflow-wrap: anywhere;
            padding: 0.25rem 0.5rem;
        }

        .style-container:has(s25-ng-document-report-builder) {
            padding: 0;

            &::after {
                --stripe-width: 1.5em;
                --stripe-color: #0001;
                --stripe-color-2: transparent;
                content: "";
                background: repeating-linear-gradient(
                    -45deg,
                    var(--stripe-color),
                    var(--stripe-color) var(--stripe-width),
                    var(--stripe-color-2) var(--stripe-width),
                    var(--stripe-color-2) calc(var(--stripe-width) * 2)
                );
            }
        }

        .style-container::after {
            position: absolute;
            height: 100%;
            width: 100%;
            top: 0;
            left: 0;
        }

        .invalid::after {
            content: "";
            background-color: color-mix(in hsl, var(--red), transparent 85%) !important;
            --stripe-color: color-mix(in hsl, var(--red), transparent 100%) !important;
            --stripe-color-2: color-mix(in hsl, var(--red), transparent 75%) !important;
        }

        .functionText {
            font-weight: bold;
            font-size: 0.9em;
            font-family: var(--font--pre), monospace;
        }

        .variable {
            font-size: 0.9em;
            font-family: var(--font--pre), monospace;
        }
    `,
    host: {
        "[attr.title]": "(editable() && cell().errorMessage()) || typeName()",
    },
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentReportBuilderCellComponent {
    readonly documentType = input.required<Doc.SubreportTypeId>();
    readonly documentScope = input.required<Doc.Scope>();
    readonly cell = input.required<ReportBuilderCell>();
    readonly editable = input.required<boolean>();
    readonly groupBy = input<string>();

    readonly typeName = computed(() => S25Util.firstCharToUpper(this.cell().variable().type()));

    readonly widthChange = output<number>();

    readonly reportBuilder = viewChild(DocumentReportBuilderComponent);

    readonly popover = viewChild(PopoverComponent);

    async hidePopoverUntil(show: Promise<{ refresh: boolean }>) {
        this.popover()?.forceClose();
        const change = await show;
        if (change.refresh) this.reportBuilder()?.document.resource.reload();
        this.popover()?.forceOpen();
    }
}
